import { sha256 } from 'js-sha256'
import { v4 } from 'uuid'

import { filterInvalidDomains, getFastestOrigin } from 'utils/fastestSpeed'

type FieldType = 'url' | 'string' | 'multi-line-string' | 'image' | 'consent' | 'boolean'

interface EditableField {
  name: string
  type: FieldType
  value: string | boolean
  optional?: boolean
}

interface ClipEntry {
  id: string
  image: string
  fields: { name: string; data: Element }[]
  originalName: string
  editableFields: EditableField[]
}

interface LoadedDocument {
  meta: EditableField[]
  clips: ClipEntry[]
}

interface ParsedXml extends LoadedDocument {
  document: Document
}

const checkResOk = async (res: Response): Promise<Response> => {
  if (res.ok) {
    return res
  } else {
    const error = new Error(`${res.status}: ${res.statusText}`)
    throw error
  }
}
const salt = new TextDecoder().decode(
  new Uint8Array(('6e6f766131363838393939'.match(/(..)/g) ?? []).map((i) => parseInt(i, 16)))
)
const generateChecksum = (str: string): string => {
  const hash = sha256.create()
  hash.update(str)
  hash.update(salt)
  const hashValue = hash.hex()
  return hashValue
}
const mod = (n: number, d: number): number => {
  return ((n % d) + d) % d
}
const OFFSET = 9487
const MAP = 56173
const INV = 7
const shuffleHex = (str: string): string => {
  const step1 = (str.match(/(....)/g) ?? []).map((c) => parseInt(c, 16))
  const step2 = step1.map((c, index) => mod((c + index + OFFSET) * MAP, 65535))
  const res = step2.map((i) => i.toString(16).padStart(4, '0')).join('')
  return res
}
const unShuffleHex = (str: string): string => {
  const step1 = (str.match(/..../g) ?? []).map((i) => parseInt(i, 16))
  const step2 = step1.map((c, index) => mod(c * INV - OFFSET - index, 65535))
  const res = step2.map((i) => i.toString(16).padStart(4, '0')).join('')
  return res
}
const shuffleString = (str: string): string => {
  const hexedText = encodeURIComponent(str)
    .split('')
    .map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('')
  const paddedHexedText = hexedText.length % 4 === 0 ? hexedText + '4444' : hexedText + '22'
  const shuffledPaddedHexedText = shuffleHex(paddedHexedText)
  return shuffledPaddedHexedText
}
const unshuffleString = (str: string): string => {
  const unshuffledHexedText = unShuffleHex(str)
  const unpaddedUnshuffledHexedText = unshuffledHexedText.slice(
    0,
    -Number(unshuffledHexedText[unshuffledHexedText.length - 1])
  )
  const decodedUnpaddedUnshuffledHexedText = decodeURIComponent(
    (unpaddedUnshuffledHexedText.match(/../g) ?? []).map((c) => String.fromCharCode(parseInt(c, 16))).join('')
  )
  return decodedUnpaddedUnshuffledHexedText
}
const findAndUpdate = (dict: Element, field: EditableField): void => {
  var _a
  const keys = dict.querySelectorAll(':scope > key')
  let target: Element | undefined = [...keys].find((k) => k.textContent === field.name)
  if (field.value === '' && !field.optional) {
    throw new Error('bad value')
  }
  if (target && typeof field.value === 'string' && field.value.trim() === '') {
    const prevNode = target.previousSibling
    const textNode = target.nextSibling
    const valueElement = target.nextElementSibling
    if ((prevNode == null ? void 0 : prevNode.nodeType) === dict.TEXT_NODE) {
      prevNode!.remove()
    }
    textNode == null ? void 0 : textNode.remove()
    target.remove()
    valueElement!.remove()
    return
  }
  if (target == null && field.value === '') {
    return
  }
  if (target == null) {
    const fragment = dict.ownerDocument!.createDocumentFragment()
    fragment.append(dict.ownerDocument!.createTextNode('\n	'))
    const newKey = dict.ownerDocument!.createElement('key')
    newKey.textContent = field.name
    fragment.appendChild(newKey)
    let newValueElement: Element
    switch (field.type) {
      case 'url':
      case 'string':
      case 'multi-line-string':
      case 'image':
        newValueElement = dict.ownerDocument!.createElement('string')
        newValueElement.textContent = field.value as string
        break
      case 'consent': {
        newValueElement = dict.ownerDocument!.createElement('dict')
        const dictChild1 = dict.ownerDocument!.createElement('key')
        dictChild1.textContent = 'default'
        const dictChild2 = dict.ownerDocument!.createElement('string')
        newValueElement.append(dict.ownerDocument!.createTextNode('\n		'))
        newValueElement.appendChild(dictChild1)
        newValueElement.append(dict.ownerDocument!.createTextNode('\n		'))
        newValueElement.appendChild(dictChild2)
        newValueElement.append(dict.ownerDocument!.createTextNode('\n	'))
        break
      }
      default:
        throw new Error('unexpected missing field')
    }
    fragment.append(dict.ownerDocument!.createTextNode('\n	'))
    fragment.appendChild(newValueElement!)
    const keys2 = [...dict.querySelectorAll(':scope > key')]
    let insertBefore: Node | null = null
    for (const k of keys2) {
      if (field.name < (k.textContent ?? '')) {
        insertBefore = k
        break
      }
    }
    if (
      ((_a = insertBefore == null ? void 0 : (insertBefore as Element).previousSibling) == null
        ? void 0
        : (_a as Node).nodeType) === document.TEXT_NODE
    ) {
      insertBefore = (insertBefore == null ? void 0 : (insertBefore as Element).previousSibling) ?? null
    }
    dict.insertBefore(fragment, insertBefore)
    target = newKey
  }
  const toChange = target.nextElementSibling!
  switch (field.type) {
    case 'url':
    case 'string':
    case 'multi-line-string':
    case 'image':
      toChange.textContent = field.value as string
      break
    case 'consent':
      toChange.querySelector(':scope string')!.textContent = field.value as string
      break
    case 'boolean':
      if (field.value) {
        toChange.replaceWith(dict.ownerDocument!.createElement('true'))
      } else {
        toChange.replaceWith(dict.ownerDocument!.createElement('false'))
      }
      break
    default:
  }
}
const randomizeUuid = (document2: Document): void => {
  const payloadUUIDKeys = [...document2.querySelectorAll('key')].filter((i) => i.textContent === 'PayloadUUID')
  for (const key of payloadUUIDKeys) {
    const uuidElement = key.nextElementSibling!
    const currentUuid = uuidElement.textContent!
    const siblingIdentifierKey = [...key.parentElement!.querySelectorAll(':scope > key')].find(
      (i) => i.textContent === 'PayloadIdentifier'
    )
    const siblingIdentifierElement = siblingIdentifierKey == null ? void 0 : siblingIdentifierKey.nextElementSibling
    const siblingIdentifier = siblingIdentifierElement!.textContent!
    const newUUID = v4().toUpperCase()
    uuidElement.textContent = newUUID
    siblingIdentifierElement!.textContent = siblingIdentifier.replace(currentUuid, newUUID)
  }
}
const parseXmlString = (data: string): ParsedXml => {
  const parser = new DOMParser()
  const xmlDocument = parser.parseFromString(data, 'application/xml')
  return { document: xmlDocument, ...loadDocument(xmlDocument) }
}
const parseXml = async (file: Blob): Promise<ParsedXml> => {
  const data = await file.text()
  return parseXmlString(data)
}
const scanFields = (dict: Element): { name: string; data: Element }[] => {
  if (dict.tagName !== 'dict') {
    return []
  }
  const list: { name: string; data: Element }[] = []
  let currentKey = ''
  let nodeIsKey = true
  for (let i = 0; i < dict.children.length; i++) {
    if (nodeIsKey) {
      currentKey = dict.children[i].textContent ?? ''
    } else {
      list.push({
        name: currentKey,
        data: dict.children[i],
      })
    }
    nodeIsKey = !nodeIsKey
  }
  return list
}
const loadDocument = (document2: Document): LoadedDocument => {
  var _a, _b, _c
  const topFields = scanFields(document2.querySelector(':root > dict')!)
  const PayloadDisplayName = topFields.find((i) => i.name === 'PayloadDisplayName')!.data.textContent!
  const PayloadIdentifier = topFields.find((i) => i.name === 'PayloadIdentifier')!.data.textContent!
  const ConsentText =
    ((_a = topFields.find((i) => i.name === 'ConsentText')) == null
      ? void 0
      : _a.data.querySelector(':scope string')!.textContent) ?? ''
  const PayloadOrganization =
    ((_b = topFields.find((i) => i.name === 'PayloadOrganization')) == null ? void 0 : _b.data.textContent) ?? ''
  const PayloadDescription =
    ((_c = topFields.find((i) => i.name === 'PayloadDescription')) == null ? void 0 : _c.data.textContent) ?? ''
  const meta: EditableField[] = [
    {
      type: 'string',
      name: 'PayloadDisplayName',
      value: PayloadDisplayName,
    },
    {
      type: 'string',
      name: 'PayloadIdentifier',
      value: PayloadIdentifier,
    },
    {
      type: 'multi-line-string',
      name: 'PayloadDescription',
      value: PayloadDescription,
      optional: true,
    },
    {
      type: 'string',
      name: 'PayloadOrganization',
      value: PayloadOrganization,
      optional: true,
    },
    {
      type: 'consent',
      name: 'ConsentText',
      value: ConsentText,
      optional: true,
    },
  ]
  const PayloadContent = topFields.find((i) => i.name === 'PayloadContent')!.data
  const clips: ClipEntry[] = [...PayloadContent.children].map((e) => {
    const list = scanFields(e)
    const rawImgText = list.find((i) => i.name === 'Icon')!.data.textContent!
    const imgText = rawImgText.replace(/[\r\n\s]/g, '')
    const src = 'data:image/png;base64,' + imgText
    const uuid = list.find((i) => i.name === 'PayloadUUID')!.data.textContent!.replace(/[\r\n\s]/g, '')
    const editableFields: EditableField[] = []
    editableFields.push({
      type: 'image',
      name: 'Icon',
      value: rawImgText,
    })
    const label = list.find((i) => i.name === 'Label')!.data.textContent!.replace(/[\r\n\s]/g, '')
    editableFields.push({
      type: 'string',
      name: 'Label',
      value: label,
    })
    const url = list.find((i) => i.name === 'URL')!.data.textContent!.replace(/[\r\n\s]/g, '')
    editableFields.push({
      type: 'url',
      name: 'URL',
      value: url,
    })
    const removeable = list.find((i) => i.name === 'IsRemovable')!.data.tagName === 'true'
    editableFields.push({
      type: 'boolean',
      name: 'IsRemovable',
      value: removeable,
    })
    const fullScreen = list.find((i) => i.name === 'FullScreen')!.data.tagName === 'true'
    editableFields.push({
      type: 'boolean',
      name: 'FullScreen',
      value: fullScreen,
    })
    return {
      id: uuid,
      image: src,
      fields: list,
      originalName: label,
      editableFields,
    }
  })
  return { meta, clips }
}
const generateNewXml = (doc: Document, meta: EditableField[], clips: ClipEntry[]): Document => {
  const newDoc = doc.cloneNode(true) as Document
  const rootList = newDoc.querySelector(':root > dict')!
  for (const f of meta) {
    findAndUpdate(rootList, f)
  }
  const keys = rootList.querySelectorAll(':scope > key')
  const contentKey = [...keys].find((i) => i.textContent === 'PayloadContent')!
  const apps = [...contentKey.nextElementSibling!.querySelectorAll(':scope > dict')]
  for (const app of apps) {
    const keys2 = app.querySelectorAll(':scope > key')
    const idKey = [...keys2].find((i) => i.textContent === 'PayloadUUID')!
    const id = idKey.nextElementSibling!.textContent!
    const targetClip = clips.find((i) => i.id === id)
    if (targetClip) {
      for (const f of targetClip.editableFields) {
        findAndUpdate(app, f)
      }
    }
  }
  randomizeUuid(newDoc)
  return newDoc
}
const installNewXml = (doc: Document, meta: EditableField[], clips: ClipEntry[]): void => {
  const xml = generateNewXml(doc, meta, clips)
  const serializer = new XMLSerializer()
  const xmlStr = serializer.serializeToString(xml)
  const blob = new Blob([xmlStr], { type: 'application/x-apple-aspen-config' })
  const url = URL.createObjectURL(blob)
  const frame = document.createElement('iframe')
  frame.style.display = 'none'
  frame.src = url
  document.body.appendChild(frame)
}
const docTypeToString = (docType: DocumentType): string =>
  '<!DOCTYPE ' +
  docType.name +
  (docType.publicId ? ' PUBLIC "' + docType.publicId + '"' : '') +
  (!docType.publicId && docType.systemId ? ' SYSTEM' : '') +
  (docType.systemId ? ' "' + docType.systemId + '"' : '') +
  '>'
const LANDING_ID_PREFIX_PARAM_NAME = 'idp'
const installMobileConf = async (
  confUrl: string,
  entryUrl: string | null,
  userId: string,
  idPrefix: string
): Promise<void> => {
  var _a
  const file = await fetch(confUrl, { cache: 'no-cache' })
  if (file.ok) {
    const blob = await file.blob()
    const parsedManifest = await parseXml(blob)
    let serviceClipVersion: string | undefined
    let mainPageURLField: EditableField | undefined
    for (const clip of parsedManifest.clips) {
      const serviceClipURL = clip.editableFields.find(
        (i) => i.name === 'URL' && i.type === 'url' && !/^https?:\/\//.test(i.value as string)
      )
      const mainPageURL = clip.editableFields.find(
        (i) => i.name === 'URL' && i.type === 'url' && /^https?:\/\//.test(i.value as string)
      )
      if (mainPageURL != null) {
        mainPageURLField = mainPageURL
      }
      if (serviceClipURL) {
        const servicePageText = await fetch(serviceClipURL.value as string)
          .then(checkResOk)
          .then((res) => res.text())
        const parsedDocument = new DOMParser().parseFromString(servicePageText, 'text/html')
        const userIdNode = parsedDocument.querySelector('script[type="x-meta"][data-name="user_id"]')
        if (userIdNode) {
          userIdNode.textContent = shuffleHex(userId)
        }
        const userIdHashNode = parsedDocument.querySelector('script[type="x-meta"][data-name="user_id_h"]')
        if (userIdHashNode) {
          userIdHashNode.textContent = generateChecksum(userId)
        }
        const userIdHPrefixNode = parsedDocument.querySelector('script[type="x-meta"][data-name="user_id_p"]')
        if (userIdHPrefixNode) {
          userIdHPrefixNode.textContent = idPrefix
        }
        const versionNode = parsedDocument.querySelector('script[type="x-meta"][data-name="conf_version"]')
        if (versionNode) {
          serviceClipVersion = (_a = versionNode.textContent) == null ? void 0 : _a.trim()
        }
        const pageCode =
          (parsedDocument.doctype ? docTypeToString(parsedDocument.doctype) + '\r\n' : '') +
          parsedDocument.documentElement.outerHTML
        serviceClipURL.value = 'data:text/html;base64,' + btoa(unescape(encodeURIComponent(pageCode)))
      }
      if (mainPageURL) {
        const parsed = new URL(mainPageURL.value as string)
        parsed.searchParams.set('id', shuffleHex(userId))
        parsed.searchParams.set('h', generateChecksum(userId))
        if (idPrefix) {
          parsed.searchParams.set(LANDING_ID_PREFIX_PARAM_NAME, idPrefix)
        }
        if (entryUrl != null) {
          parsed.host = new URL(entryUrl).host
        }
        mainPageURL.value = parsed.toString()
      }
    }
    if (mainPageURLField != null && serviceClipVersion != null) {
      const parsed = new URL(mainPageURLField.value as string)
      parsed.searchParams.set('conf_version', serviceClipVersion)
      mainPageURLField.value = parsed.toString()
    }
    installNewXml(parsedManifest.document, parsedManifest.meta, parsedManifest.clips)
  }
}
const TIMEOUT_MS = 5e3
const getMetadataJSONWithUrl = async (url: string): Promise<any> => {
  const controller = new AbortController()
  const signal = controller.signal
  setTimeout(() => {
    controller.abort('timeout')
  }, TIMEOUT_MS)
  return JSON.parse(await processMetaRes(await fetch(url, { cache: 'no-cache', signal }).then(checkResOk)))
}
const getClearTextMobileConfTextWithUrl = async (url: string): Promise<string> => {
  const res = await fetch(url, { cache: 'no-cache' }).then(checkResOk)
  const arrayBuffer = await res.arrayBuffer()
  const uint8 = new Uint8Array(arrayBuffer)
  return utf8decoder.decode(uint8)
}
function base64_encode(s: string): string {
  return btoa(unescape(encodeURIComponent(s)))
}
const utf8decoder = new TextDecoder()
const processMetaRes = async (res: Response): Promise<string> => {
  const arrayBuffer = await res.arrayBuffer()
  const uint8 = new Uint8Array(arrayBuffer)
  const k = 168
  for (let i = 0; i < uint8.length; i++) {
    uint8[i] = uint8[i] ^ k
  }
  return utf8decoder.decode(uint8)
}
const precachePreloadConfig = async (preloadPath: string, localStorageKey: string): Promise<void> => {
  try {
    let config: any
    config = await getMetadataJSONWithUrl(preloadPath)
    const shuffledHexedConfig = shuffleString(JSON.stringify(config))
    localStorage.setItem(localStorageKey, shuffledHexedConfig)
  } catch (err) {
    console.log(err)
  }
}
const installFromPreload = async (
  oldId: string,
  preloadPath: string,
  localStorageKey: string,
  avoidDomains: string[] = [],
  idPrefix: string
): Promise<void> => {
  let config: any
  try {
    config = await getMetadataJSONWithUrl(preloadPath)
  } catch (err) {
    const cachedConfig = localStorage.getItem(localStorageKey)
    if (cachedConfig) {
      config = JSON.parse(unshuffleString(cachedConfig))
    } else {
      throw err
    }
  }
  const entries = filterInvalidDomains(config.entries ?? [])
  const entriesExcludesAvoided = entries.filter((entry) => !avoidDomains.includes(new URL(entry).host))
  let selectedUrl: string | null = null

  if (entries.length > 0) {
    try {
      selectedUrl = await getFastestOrigin(entriesExcludesAvoided.length > 0 ? entriesExcludesAvoided : entries, {
        timeoutMs: TIMEOUT_MS,
      })
    } catch (err) {
      if (entriesExcludesAvoided.length > 0 && entriesExcludesAvoided.length !== entries.length) {
        try {
          selectedUrl = await getFastestOrigin(entries, { timeoutMs: TIMEOUT_MS })
        } catch (fallbackErr) {
          console.warn('[installFromPreload] unable to detect fastest preload entry', fallbackErr)
        }
      } else {
        console.warn('[installFromPreload] unable to detect fastest preload entry', err)
      }
    }
  }
  let mobileConfUrl: string
  {
    const mobileConfText = await getClearTextMobileConfTextWithUrl(config.ipa_download_urls[0])
    mobileConfUrl = 'data:application/x-apple-aspen-config;base64,' + base64_encode(mobileConfText)
  }
  return installMobileConf(mobileConfUrl, selectedUrl, oldId, idPrefix)
}
const getMetadataJSONWithUrlWithCache = async (url: string, localStorageKey: string): Promise<any> => {
  try {
    return await getMetadataJSONWithUrl(url)
  } catch (err) {
    const cachedConfig = localStorage.getItem(localStorageKey)
    if (cachedConfig) {
      return JSON.parse(unshuffleString(cachedConfig))
    } else {
      throw err
    }
  }
}

interface CanInstallResult {
  canInstall: boolean
  forceVersion: string | null
  configVersion: string | null
}

const canInstallNewConfig = async (preloadPath: string): Promise<CanInstallResult> => {
  var _a, _b, _c
  try {
    let config: any
    if ('') {
    } else {
      config = await getMetadataJSONWithUrl(preloadPath)
    }
    const h5ForceVersion: string | null =
      ((_b = (_a = config.device) == null ? void 0 : _a.h5) == null ? void 0 : _b.force_version) ?? null
    let mobileConfText: string
    if ('') {
    } else {
      mobileConfText = await getClearTextMobileConfTextWithUrl(config.ipa_download_urls[0])
    }
    const { clips } = parseXmlString(mobileConfText!)
    let h5ConfigVersion: string | null = null
    for (const clip of clips) {
      for (const field of clip.editableFields) {
        if (field.type === 'url' && (field.value as string).startsWith('data:')) {
          const dataUri = new URL(field.value as string)
          const servicePageText = await fetch(dataUri.toString())
            .then(checkResOk)
            .then((res) => res.text())
          const parsedDocument = new DOMParser().parseFromString(servicePageText, 'text/html')
          const versionNode = parsedDocument.querySelector('script[type="x-meta"][data-name="conf_version"]')
          if (versionNode != null) {
            h5ConfigVersion = ((_c = versionNode.textContent) == null ? void 0 : _c.trim()) ?? null
          }
          break
        }
      }
    }
    return {
      canInstall: true,
      forceVersion: h5ForceVersion,
      configVersion: h5ConfigVersion,
    }
  } catch (err) {
    return {
      canInstall: false,
      forceVersion: null,
      configVersion: null,
    }
  }
}
export {
  canInstallNewConfig,
  generateChecksum,
  getMetadataJSONWithUrl,
  getMetadataJSONWithUrlWithCache,
  installFromPreload,
  precachePreloadConfig,
  shuffleHex,
  unShuffleHex,
}
